// src/utils/ws_whatsapp.js
// Cliente WebSocket para el estado real de los mensajes de WhatsApp.
//
// El backend recibe de Meta el ciclo de vida de cada mensaje (enviado →
// entregado → leído, o fallido) y lo empuja por este canal. Sin esto solo
// sabríamos que Meta aceptó la petición, no que el cliente lo recibió.
//
// A diferencia del PDS, aquí puede haber varias vistas escuchando a la vez
// (un modal abierto, la tabla de pedidos), así que se maneja un único socket
// compartido con lista de suscriptores en vez de una sola callback.

let socket = null;
let reconnectTimer = null;
let retries = 0;

const suscriptores = new Set();

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

const WS_BASE =
  process.env.VUE_APP_WS ||
  `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;

/**
 * Registra una callback que recibirá cada mensaje del canal.
 * Devuelve la función para darse de baja — llamarla al desmontar el componente.
 *
 * @param {Function} onMessage - recibe el objeto JSON parseado
 * @returns {Function} baja
 */
export function suscribirWhatsapp(onMessage) {
  suscriptores.add(onMessage);

  // El socket se abre con el primer suscriptor, no al cargar la app.
  if (!socket) _connect();

  return () => {
    suscriptores.delete(onMessage);
    // Sin nadie escuchando no tiene sentido mantener la conexión abierta.
    if (suscriptores.size === 0) cerrarWhatsapp();
  };
}

function _connect() {
  socket = new WebSocket(`${WS_BASE}/ws/whatsapp/`);

  socket.onopen = () => {
    retries = 0;
    _clearReconnectTimer();
    console.info("[WS WhatsApp] Conectado");
  };

  socket.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      console.error("[WS WhatsApp] Error parseando mensaje:", e);
      return;
    }
    if (data.tipo === "ping") return; // heartbeat del servidor
    suscriptores.forEach((fn) => {
      try {
        fn(data);
      } catch (e) {
        console.error("[WS WhatsApp] Error en suscriptor:", e);
      }
    });
  };

  socket.onclose = (event) => {
    // 1000 = cierre limpio nuestro (cerrarWhatsapp), no reconectar.
    // Tampoco reconectar si ya nadie está escuchando.
    if (event.code === 1000 || suscriptores.size === 0) return;

    retries++;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** (retries - 1), RECONNECT_MAX_MS);
    console.warn(`[WS WhatsApp] Reconectando en ${delay}ms (intento ${retries})`);
    reconnectTimer = setTimeout(_connect, delay);
  };

  socket.onerror = (error) => {
    console.error("[WS WhatsApp] Error:", error);
  };
}

/** Cierra la conexión limpiamente, sin reconexión automática. */
export function cerrarWhatsapp() {
  _clearReconnectTimer();
  retries = 0;
  if (socket) {
    socket.close(1000, "Cierre manual");
    socket = null;
  }
}

function _clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}
